import React, { Component } from 'react';
import { toJS } from 'mobx';
import {
  inject,
  observer,
} from 'mobx-react';
import {
  Alert,
  Button,
  Col,
  ControlLabel,
  Form,
  FormControl,
  FormGroup,
  Row,
} from 'react-bootstrap';
import startCase from 'lodash/startCase';
import omit from 'lodash/omit';
import LoadingButton from '../../components/LoadingButton/LoadingButton';
import { DEFAULT_ENUM } from '../../constants/BatchTypeEnums';
import {
  Checkbox,
  InputGroup,
} from 'react-bootstrap';
import { DeviceTypeEnum } from '../../constants/BatchTypeEnums';
import logger from '../../utils/logger';
import {
  Field,
  FieldArray,
  Formik,
} from 'formik';
import { withTranslation } from 'react-i18next';
import JogTable from '../Materials/JogTable';
import {
  DeviceTypes,
  FIELDS_FOR_TENANTS,
} from '../../constants/DeviceModels';
import Tabs from 'react-bootstrap/lib/Tabs';
import Tab from 'react-bootstrap/lib/Tab';
import AuditLog from '../../components/AuditLog/AuditLog';
import { PERMISSION } from '../../modules/newAuth/model/User';
import { isFeatureEnabled } from '../../utils/feature-flags';
import { isModuleVisible } from '../../utils/module-controller';
import { showConfirmModal } from '../../modules/modals/confirmService';
import { IOChange } from '../../components/IOChange/IOChange';
import { ConfirmWithInitials } from '../../components/Confirm/ConfirmWithInitials';
import { BatchEventType } from '../../constants/BatchEventType';
import Filters from '../../components/Filters/Filters';

const FEATURE_AUTO_TUNING = isFeatureEnabled('auto_tuning');

class EditDevice extends Component {
  constructor(props) {
    super(props);

    const {
      editingDevice,
      editingDeviceId,
      editingDeviceTypeName: deviceType,
      editingDeviceModel: model,
    } = this.props.devicesStore;

    this.state = {
      model: null,
      device: null,
      deviceType,
      error: null,
      errors: {},
      jogTableVisible: false,
      ioTested: false,
      confirmIOTested: false,
      filter: '',
    };

    if (!model) {
      logger.warn(`device model for ${deviceType} not found!`);
      return;
    }

    this.state.model = {
      description: {
        label: 'Name',
        type: 'text',
        required: true,
        maxlength: 15,
        col: 12,
      },
      ...model,
    };

    if (editingDevice) {
      this.state = {
        ...this.state,
        id: editingDeviceId,
        device: toJS(editingDevice),
      };
    } else {
      let device = {};
      Object.keys(model).forEach(fieldName => {
        device[fieldName] = model[fieldName].defaultValue;
      });

      this.state = {
        ...this.state,
        id: null,
        device,
      };
    }
  }

  componentDidMount () {
    this.mounted = true;
  }

  componentWillUnmount () {
    this.mounted = false;
  }

  isFieldValid(fieldName, values) {
    const { model } = this.state;

    if (!model[fieldName]) {
      return true;
    } else if (model[fieldName].required && !values[fieldName]) {
      return false;
    } else if (model[fieldName].maxlength && values[fieldName].length > model[fieldName].maxlength) {
      return false;
    } else if (!isNaN(model[fieldName].min) && Number(values[fieldName]) < model[fieldName].min) {
      return false;
    } else if (isNaN(model[fieldName].min) && Number(values[fieldName]) < Number(values[model[fieldName].min])) {
      return false;
    } else if (!isNaN(model[fieldName].max) && Number(values[fieldName]) > model[fieldName].max) {
      return false;
    } else if (isNaN(model[fieldName].max) && Number(values[fieldName]) > Number(values[model[fieldName].max])) {
      return false;
    }

    return true;
  }

  isValid(values) {
    const { model } = this.state;
    let valid = true;

    Object.keys(model).forEach(fieldName => {
      if (!this.isFieldValid(fieldName, values)) {
        valid = false;
      }
      // input elements should not switch from uncontrolled to controlled,
      // we need to set value "zero" not "null" for input type "number"
      if (model[fieldName] && model[fieldName].type === 'number' && !values[fieldName]) {
        values[fieldName] = 0;
      }
    });

    return valid;
  }

  onJogTableClick() {
    const { jogTableVisible, id } = this.state;
    const { devicesStore } = this.props;

    this.setState({
      jogTableVisible: !jogTableVisible,
      device: toJS(devicesStore.getDeviceById(id)),
    });
  };

  getIOChangeList(values) {
    const { devicesStore } = this.props;
    const { editingDevice } = devicesStore;
    const fieldsToCheck = new Set(['gatePNo', 'gateOnePNo', 'scaleId']);
    let list = [];

    if (!editingDevice || ![DeviceTypes.Bin, DeviceTypes.Pump].includes(editingDevice.deviceTypeName)) {
      return list;
    }

    const getValueToTest = (key, values) => {
      if (key === 'scaleId') {
        const scaleId = Number(values[key]);
        const scale = devicesStore.getDeviceById(scaleId);
        const prevScaleId = editingDevice.scaleId;
        const prevScale = devicesStore.getDeviceById(prevScaleId);
        return {
          value: scale?.description,
          oldValue: prevScale?.description,
          testValue: editingDevice?.gateOnePNo,
        };
      }
      return {
        value: Number(values[key]),
        oldValue: Number(editingDevice[key]),
        testValue: Number(values[key]),
      };
    };

    Object.keys(values).forEach((key) => {
      if (fieldsToCheck.has(key) && Number(editingDevice[key]) !== Number(values[key])) {
        list.push({
          name: key,
          type: editingDevice.deviceTypeName,
          ...getValueToTest(key, values),
        });
      }
    });

    return list;
  }

  onSave = (values) => {
    const { deviceType, ioTested, confirmIOTested } = this.state;
    const { devicesStore, batchStore, t } = this.props;
    const { editingDeviceId } = devicesStore;
    let promise;

    this.setState({ error: null, errors: {}, saving: true });

    if (!this.isValid(values)) {
      this.setState({ error: t('Please fill in all required fields!'), saving: false });
      return;
    }

    const changeList = this.getIOChangeList(values);
    const hasIOChanged = changeList.length > 0;

    if (hasIOChanged && !ioTested) {
      const onIOTest = () => {
        this.setState({ ioTested: true });
      };

      showConfirmModal({
        title: t('The I/O has been changed'),
        message: <IOChange changeList={changeList} onIOTest={onIOTest} />,
        okLabel: t('Yes'),
        cancelLabel: t('No'),
        enableEscape: false,
      })
        .then(() => {
          setTimeout(() => {
            this.onSave(values);
          }, 1000);
        })
        .catch((e) => {
          this.setState({ ioTested: false });
          logger.warn(e);
        })
        .finally(() => {
          if (this.mounted) {
            this.setState({ saving: false });
          }
        });
      return;
    }

    if (hasIOChanged && ioTested && !confirmIOTested) {
      const onIOConfirm = (e) => {
        this.setState({ confirmIOTested: true });
        values.lastTested = {
          eventType: BatchEventType.IO_CHANGE,
          author: e.target.value,
          time: new Date().toISOString(),
        };
      };

      showConfirmModal({
        title: t('Confirm the change'),
        message: <ConfirmWithInitials onConfirm={onIOConfirm} />,
        okLabel: t('OK'),
        cancelLabel: t('No'),
        enableEscape: false,
      })
        .then(() => {
          setTimeout(() => {
            this.onSave(values);
          }, 1000);
        })
        .catch((e) => {
          logger.warn(e);
          this.setState({ ioTested: false, confirmIOTested: false });
        })
        .finally(() => {
          if (this.mounted) {
            this.setState({ saving: false });
          }
        });
      return;
    }

    const payload = omit(values, ['linkId']);

    if (editingDeviceId) {
      promise = devicesStore.updateDevice(editingDeviceId, deviceType, payload);

      const d = devicesStore.getDeviceById(values.id);

      if (payload.meterPNo && d && payload.meterPNo !== d.meterPNo) {
        batchStore.setMeterInputs([payload.meterPNo]);
      }
    } else {
      promise = devicesStore.createDevice(deviceType, payload);

      if (payload.meterPNo) {
        batchStore.setMeterInputs([payload.meterPNo]);
      }
    }

    return promise
      .then(() => {
        devicesStore.cancelEdit();
      })
      .catch(e => {
        if (this.mounted) {
          this.setState({ error: e.message, errors: e.errors || {}, saving: false });
        }
      });
  };

  getFieldConfig = (fieldName, model = this.state.model) => {
    const fieldConfig = model[fieldName];
    if (!fieldConfig) {
      logger.warn(`No config for ${fieldName}`, model);
      return;
    }

    if (typeof fieldConfig === 'string') {
      return {
        label: startCase(fieldName),
        type: fieldConfig,
        col: 4,
        disabled: false,
        required: false,
      };
    }

    return {
      type: fieldConfig.type || 'text',
      label: fieldConfig.label || startCase(fieldName),
      col: fieldConfig.col || 4,
      disabled: fieldConfig.disabled || false,
      dependsOn: fieldConfig.dependsOn,
      dependsOnOneOf: fieldConfig.dependsOnOneOf,
      hideOn: fieldConfig.hideOn,
      unit: fieldConfig.unit || '',
      min: fieldConfig.min,
      max: fieldConfig.max,
      maxlength: fieldConfig.maxlength || 1000,
      step: fieldConfig.step || 0.1,
      defaultValue: fieldConfig.defaultValue || '',
      fieldOptions: fieldConfig.fieldOptions,
      required: fieldConfig.required || false,
      data: fieldConfig.data || [],
      hidden: fieldConfig.hidden || false,
      permission: fieldConfig.permission || null,
      hiddenModule: fieldConfig.hiddenModule || '',
      noEmptyOption: fieldConfig.noEmptyOption || false,
      showForTenants: fieldConfig.showForTenants || [],
    };

  };

  renderField(name, values, config, handleChange) {
    const { devicesStore } = this.props;
    const { editingDeviceId } = devicesStore;
    const { deviceType } = this.state;
    const value = values[name];
    let { type, disabled, min, max, required, step, maxlength, noEmptyOption } = config;

    if (typeof disabled === 'string') {
      if (disabled === 'autoTuning') {
        disabled = FEATURE_AUTO_TUNING ? values[disabled] : false;
      } else {
        disabled = values[disabled] || false;
      }
    }

    switch (type) {
      case 'number':
      case 'text':
        return (
          <FormControl
            name={name}
            type={type}
            value={type === 'number' ? value || 0 : value || ''}
            disabled={disabled}
            required={required}
            min={min}
            max={max}
            maxLength={maxlength}
            step={step}
            onChange={handleChange}
          />
        );
      case 'color':
        return (
          <FormControl
            name={name}
            type={type}
            value={value || '#000000'}
            disabled={disabled}
            required={required}
            onChange={handleChange}
          />
        );
      case 'boolean':
        return (
          <Checkbox
            inline
            name={name}
            value={value}
            checked={!!value}
            disabled={disabled}
            onChange={handleChange}
          />
        );
      case 'array':
        return (this.renderArray(name, values, config, handleChange));
      default:
        let options = devicesStore[type] ? [].concat(devicesStore[type]) : DeviceTypeEnum[type] || DEFAULT_ENUM;
        const isPort = type.indexOf('Ports') > -1;
        const isInputPort = type.indexOf('inputPorts') > -1;

        if (isPort && value && !options.find(v => Number(v.value) === Number(value))) {
          if (value > 0) {
            options.push({value, text: value});
          }
          options.sort((x, y) => x.value - y.value);
        }

        if ((name === 'parentBeltId' && deviceType === DeviceTypes.Belt)
          || (name === 'parentScaleId' && deviceType === DeviceTypes.Scale)) {
          options = options.filter(v => v.value !== editingDeviceId);
        }

        return (
          <FormControl
            name={name}
            value={value || ''}
            onChange={handleChange}
            componentClass="select"
            disabled={disabled}
            required={required}
          >
            {!noEmptyOption && (
              <option value="">- Select -</option>
            )
            }
            {isInputPort && <option value="-1">Disabled</option>}
            {options && options.map((option, i) => (
              <option key={i} value={option.value || option.id}>
                {option.symbol || option.name || option.text || option.id}
              </option>
            ))}
          </FormControl>
        );
    }
  }

  renderArray(name, values, config, handleChange) {
    const { t } = this.props;

    if (!Array.isArray(values[name])) {
      values[name] = [];
    }

    return (
      <React.Fragment>
        <FieldArray
          name={name}
          render={arrayHelpers => (
            <div className="field-array">
              {values[name] && values[name].length ? (
                values[name].map((item, index) => (
                  <Row key={index}>
                    {Object.keys(item).map((prop, i) => (
                      <Col key={i} sm={4} xs={5}>
                        <FormGroup>
                          <ControlLabel>{t(startCase(prop))}</ControlLabel>
                          {config.fieldOptions && Array.isArray(config.fieldOptions[prop])
                            ?
                            (
                              <FormControl
                                name={`${name}.${index}.${prop}`}
                                value={values[name][index][prop] || ''}
                                componentClass="select"
                                onChange={handleChange}
                              >
                                <option value="">- Select -</option>
                                {config.fieldOptions[prop].map((option) => (
                                  <option key={option} value={option}>
                                    {startCase(option)}
                                  </option>
                                ))}
                              </FormControl>
                            )
                            :
                            (
                              <Field name={`${name}.${index}.${prop}`}
                                type="number"
                                value={values[name][index][prop] || ''}
                                className="form-control input-sm" />
                            )
                          }
                        </FormGroup>
                      </Col>
                    ))}
                    <Col sm={4} xs={2}>
                      <a className="btn btn-sm btn-default btn-action"
                        title={t('Remove')}
                        onClick={() => arrayHelpers.remove(index)}>
                        <i className="fa fa-remove"/>
                      </a>
                    </Col>
                  </Row>
                ))
              ) : '' }

              <Row>
                <Col sm={3}>
                  <a className="btn btn-sm btn-default btn-add"
                    title={t('Add')} onClick={() => arrayHelpers.push(config.defaultValue)}>
                    <i className="fa fa-plus"/>
                  </a>
                </Col>
              </Row>
            </div>
          )}
        />
      </React.Fragment>
    );
  }

  renderFields(fieldName, model, values, handleChange) {
    const { devicesStore, newAuthStore, t } = this.props;
    const { errors } = this.state;
    const fieldConfig = this.getFieldConfig(fieldName, model);

    function shouldBeHidden(fieldName, fieldConfig) {
      let hidden = false;

      if (fieldConfig.hidden) {
        hidden = fieldConfig.hidden;
      }

      if (fieldConfig.dependsOn && fieldConfig.dependsOnOneOf?.length) {
        hidden = !fieldConfig.dependsOnOneOf.includes(values[fieldConfig.dependsOn]);
      } else if (fieldConfig.dependsOn) {
        hidden = !values[fieldConfig.dependsOn];
      }

      if (fieldConfig.showForTenants?.length) {
        hidden = !fieldConfig.showForTenants.includes(devicesStore.currentTenantSlug);
      }

      if (fieldConfig.hideOn) {
        hidden = !!values[fieldConfig.hideOn];
      }

      if (fieldConfig.permission) {
        hidden = !newAuthStore.user?.hasAccess(fieldConfig.permission);
      }

      if (fieldConfig.hiddenModule) {
        hidden = !isModuleVisible(fieldConfig.hiddenModule);
      }

      return hidden;
    }

    function getUnit(unit, bin) {
      let u;

      if (unit === 'scale unit') {
        let scale;

        if (bin && bin.scaleId) {
          scale = devicesStore.getDeviceById(bin.scaleId);
        }

        return scale ? devicesStore.getUnitForScale(scale.id) : unit;

      }

      u = devicesStore.units.find(v => v.id === Number(values[unit]));

      return u ? u.symbol || u.name : unit;
    }

    return shouldBeHidden(fieldName, fieldConfig) ? '' : (
      <Col sm={fieldConfig.col} xs={6} key={fieldName} className={!this.isFieldValid(fieldName, values) || errors[fieldName] ? 'has-error' : ''}>
        <ControlLabel>{t(fieldConfig.label)}</ControlLabel>
        {!fieldConfig.unit
          ? this.renderField(fieldName, values, fieldConfig, handleChange)
          : (
            <FormGroup>
              <InputGroup>
                {this.renderField(fieldName, values, fieldConfig, handleChange)}
                <InputGroup.Addon>
                  {Array.isArray(fieldConfig.unit)
                    ? `${getUnit(fieldConfig.unit[0], values)} / ${getUnit(fieldConfig.unit[1], values)}`
                    : getUnit(fieldConfig.unit, values)
                  }
                </InputGroup.Addon>
              </InputGroup>
            </FormGroup>
          )
        }
      </Col>
    );
  }

  handleFilterChange = (e) => {
    const filter = e.target.value;
    this.setState({ filter });
  };

  renderForm() {
    const { device, model, saving, error, filter } = this.state;
    const { t, devicesStore } = this.props;

    return (
      <Formik
        onSubmit={(values) => {
          this.onSave(values);
        }}
        initialValues={{
          ...device,
        }}
      >{({
          handleSubmit,
          handleChange,
          values }) => (
          <Form className="edit-device-form">
            <Row className="form-fields">
              <Col sm={12}>
                <FormGroup>
                  <FormControl
                    type="text"
                    value={filter}
                    placeholder={t('Search')}
                    onChange={this.handleFilterChange}
                  />
                </FormGroup>
              </Col>
            </Row>
            <Row className="form-fields">
              <Filters filter={filter} data={model} keys={['label']} renderData={(data) => (
                <>
                  {Object.keys(data).map(fieldName => {
                    return this.renderFields(fieldName, data, values, handleChange);
                  })}
                </>
              )}/>
            </Row>
            <Row className="form-buttons">
              <Col sm={12}>{!!error && <Alert bsStyle="danger">{error}</Alert>}</Col>
              <Col sm={6}>
                {
                  values.autoTuning
                  && values.jogTableAuto
                  && !FIELDS_FOR_TENANTS.includes(devicesStore.currentTenantSlug)
                    ? ''
                    : (
                      <>
                        {device.deviceTypeName === DeviceTypes.Bin && (
                          <Button onClick={() => this.onJogTableClick()}
                            title={t('Jog Table')}>{t('Jog Table')}
                          </Button>
                        )}
                      </>
                    )
                }
              </Col>
              <Col sm={6} className="text-right">
                <Button type="button"  onClick={() => devicesStore.cancelEdit()}>
                  {t('Close')}
                </Button>
                <LoadingButton
                  bsStyle="primary"
                  type="submit"
                  label={t('Save')}
                  loadingLabel={t('Saving')}
                  onClick={handleSubmit}
                  isLoading={saving}
                />
              </Col>
            </Row>
          </Form>
        )}
      </Formik>
    );
  }

  render() {
    const { devicesStore, newAuthStore, t } = this.props;
    const { jogTableVisible, model } = this.state;
    const {
      editingDeviceTypeName,
      editingDeviceId,
      editingDevice,
      isEditing,
    } = devicesStore;
    const canEditDevices = newAuthStore.user?.hasAccess(PERMISSION.BATCH_DEVICE);

    if (!canEditDevices) {
      return <div className="alert alert-danger">{t('No Access to Devices')}</div>;
    }

    if (!model) {
      return <div className="alert alert-danger">Device model not found</div>;
    }

    return !isEditing ? '' : (
      <div className="device-edit fade-in">
        <Row className="form-header">
          <Col sm={12}>
            <h4 onClick={() => jogTableVisible ? this.onJogTableClick() : devicesStore.cancelEdit()}>
              <i className="fa fa-arrow-left"/>{' '}
              {editingDeviceId ? t('Edit') : t('New')} {startCase(editingDeviceTypeName)}{' '} {editingDeviceId ? `#${editingDeviceId}` : ''}
            </h4>
          </Col>
        </Row>
        <Tabs
          id={'device-settings-tabs'}
          defaultActiveKey={1}
        >
          <Tab
            eventKey={1}
            title={t('Settings')}
          >
            {jogTableVisible ? (
              <JogTable
                device={editingDevice}
                onClose={() => this.onJogTableClick()}
              />
            )
              :
              this.renderForm()
            }
          </Tab>
          <Tab
            eventKey={2}
            disabled={!editingDeviceId}
            title={t('Audit Log')}
          >
            {editingDeviceId && (
              <AuditLog
                modelId={editingDeviceId}
                modelName={editingDeviceTypeName}
              />
            )}
          </Tab>
        </Tabs>

      </div>
    );
  }
}

export default withTranslation()(inject('devicesStore', 'batchStore', 'newAuthStore')(observer(EditDevice)));
